import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ToastDeduplicator } from '@/utils/toastDeduplication';

interface MessageNotification {
  id: string;
  tenant_id: string;
  conversation_id: string;
  message_id: string;
  customer_id: string;
  sender_type: string;
  message_type: string;
  external_id?: string;
  notification_type: 'new_message' | 'status_update' | 'read_update';
  metadata: unknown;
  created_at: string;
}

interface UseRealtimeNotificationsProps {
  conversationId?: string;
  onNewMessage?: (notification: MessageNotification) => void;
  onStatusUpdate?: (notification: MessageNotification) => void;
  onMessageRead?: (notification: MessageNotification) => void;
  enabled?: boolean;
}

export function useRealtimeNotifications({
  conversationId,
  onNewMessage,
  onStatusUpdate,
  onMessageRead,
  enabled = true
}: UseRealtimeNotificationsProps) {
  const { t } = useTranslation('conversations');
  const { toast } = useToast();
  const toastDeduplicator = ToastDeduplicator.getInstance();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const instanceIdRef = useRef(`realtime-${Math.random().toString(36).substring(7)}`);

  // Phase 2.5: Use ref for conversationId to prevent subscription teardown
  const conversationIdRef = useRef(conversationId);

  // Store handlers in refs to prevent subscription restarts
  const handlersRef = useRef({
    onNewMessage,
    onStatusUpdate,
    onMessageRead
  });

  // Update conversationId ref when it changes
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Update refs when handlers change
  useEffect(() => {
    handlersRef.current.onNewMessage = onNewMessage;
    handlersRef.current.onStatusUpdate = onStatusUpdate;
    handlersRef.current.onMessageRead = onMessageRead;
  }, [onNewMessage, onStatusUpdate, onMessageRead]);

  const handleNotification = useCallback((payload: { new: MessageNotification }) => {
    const notification = payload.new;
    
    console.log(`[${instanceIdRef.current}] 🔔 Real-time notification received:`, {
      type: notification.notification_type,
      conversationId: notification.conversation_id,
      messageId: notification.message_id,
      senderType: notification.sender_type,
      metadata: notification.metadata,
      timestamp: new Date().toISOString()
    });

    // Phase 2.5: Filter by conversation using ref (no dependency)
    if (conversationIdRef.current && notification.conversation_id !== conversationIdRef.current) {
      console.log(`[${instanceIdRef.current}] ⏭️ Notification filtered out (wrong conversation)`);
      return;
    }

    // Route to appropriate handler
    try {
      switch (notification.notification_type) {
        case 'new_message':
          console.log(`[${instanceIdRef.current}] 📨 Processing new message notification - TRIGGERING REFRESH`);
          handlersRef.current.onNewMessage?.(notification);
          
          // Show toast for customer messages only
          if (notification.sender_type === 'customer') {
            console.log(`[${instanceIdRef.current}] 🔔 Showing toast for customer message`);
            toast({
              title: t('realtime.newMessage'),
              description: `New customer message`,
              duration: 3000,
            });
          }
          break;
          
        case 'status_update':
          console.log(`[${instanceIdRef.current}] 📊 Processing status update notification`);
          handlersRef.current.onStatusUpdate?.(notification);
          break;
          
        case 'read_update':
          console.log(`[${instanceIdRef.current}] 👁️ Processing read update notification`);
          handlersRef.current.onMessageRead?.(notification);
          break;
          
        default:
          console.log(`[${instanceIdRef.current}] ❓ Unknown notification type: ${notification.notification_type}`);
      }
    } catch (error) {
      console.error(`[${instanceIdRef.current}] ❌ Error handling notification:`, error);
    }
  }, [toast]);  // Phase 2.5: Removed conversationId dependency - now uses ref!

  useEffect(() => {
    if (!enabled) {
      console.log(`[${instanceIdRef.current}] ⏸️ Real-time notifications disabled`);
      return;
    }

    console.log(`[${instanceIdRef.current}] 🚀 Setting up real-time notifications`);
    console.log(`[${instanceIdRef.current}] 🎯 Filter: ${conversationId ? `conversation ${conversationId}` : 'all conversations'}`);

    // Create channel for message notifications
    const channel = supabase
      .channel('message-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_notifications'
        },
        handleNotification
      )
      .subscribe((status) => {
        console.log(`[${instanceIdRef.current}] 📡 Real-time subscription status:`, status);
        
        if (status === 'SUBSCRIBED') {
          console.log(`[${instanceIdRef.current}] ✅ Real-time notifications active`);
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`[${instanceIdRef.current}] ❌ Real-time subscription error`);
          // Use deduplication for connection error toasts
          if (toastDeduplicator.canShowToast('realtime-error', t('realtime.connectionIssueTitle'))) {
            const result = toast({
              title: t('realtime.connectionIssueTitle'),
              description: t('realtime.connectionIssueDesc'),
              variant: "destructive",
              duration: 5000,
            });
            if (result?.id) {
              toastDeduplicator.registerToast('realtime-error', t('realtime.connectionIssueTitle'), result.id);
            }
          }
        }
      });

    channelRef.current = channel;

    // Cleanup function
    return () => {
      console.log(`[${instanceIdRef.current}] 🧹 Cleaning up real-time notifications`);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [enabled, handleNotification]);

  // Manual cleanup function
  const cleanup = useCallback(() => {
    console.log(`[${instanceIdRef.current}] 🧹 Manual cleanup triggered`);
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  // Phase 2.5: Wrap return value in useMemo to prevent external cleanup triggers
  // This ensures the returned object has a stable reference across renders
  return useMemo(() => ({ cleanup }), [cleanup]);
}